import React, { useCallback, useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, ArrowRight } from 'lucide-react';
import { NAV_LINKS, LOGO } from '../data/mock';

function Brand({ small }) {
  return (
    <Link to="/" className="flex items-center gap-2.5 group">
      <img src={LOGO} alt="Hybrid Training Club" className={`${small ? 'h-8 w-8' : 'h-10 w-10'} object-contain transition-transform group-hover:scale-105`} />
      <span className={`font-display ${small ? 'text-xl' : 'text-xl md:text-2xl'} leading-none`}>
        HYBRID<span className="text-gold">.</span>
      </span>
      {!small && (
        <span className="hidden sm:block font-cond text-[10px] tracking-[0.3em] text-[var(--hx-muted)] leading-none pt-1">
          TRAINING<br />CLUB
        </span>
      )}
    </Link>
  );
}

function ClubsDropdown({ link, open, setOpen }) {
  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button className="font-cond text-sm tracking-wider text-[var(--hx-text)] hover:text-accent transition-colors flex items-center gap-1">
        {link.label}
        <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      <div className={`absolute top-full left-1/2 -translate-x-1/2 pt-3 transition-all duration-200 ${open ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'}`}>
        <div className="bg-[#131313] border border-[#262626] rounded-md overflow-hidden min-w-[200px] shadow-2xl">
          {link.children.map((c) => (
            <NavLink key={c.to} to={c.to} className="block px-5 py-3 font-cond text-sm tracking-wider text-[var(--hx-muted)] hover:text-accent hover:bg-[#1c1c1c] transition-colors">
              {c.label}
            </NavLink>
          ))}
        </div>
      </div>
    </div>
  );
}

function DesktopNav({ clubsOpen, setClubsOpen }) {
  return (
    <nav className="hidden lg:flex items-center gap-7">
      {NAV_LINKS.map((link) =>
        link.children ? (
          <ClubsDropdown key={link.label} link={link} open={clubsOpen} setOpen={setClubsOpen} />
        ) : (
          <NavLink
            key={link.to}
            to={link.to}
            className={({ isActive }) =>
              `font-cond text-sm tracking-wider transition-colors hover:text-accent ${isActive ? 'text-accent' : 'text-[var(--hx-text)]'}`
            }
          >
            {link.label}
          </NavLink>
        )
      )}
    </nav>
  );
}

function MobileMenu({ open, onClose }) {
  return (
    <div className={`fixed inset-0 z-50 lg:hidden transition-all duration-300 ${open ? 'visible' : 'invisible'}`}>
      <div className={`absolute inset-0 bg-black/70 transition-opacity ${open ? 'opacity-100' : 'opacity-0'}`} onClick={onClose} />
      <div className={`absolute right-0 top-0 h-full w-[82%] max-w-sm bg-[#0d0d0d] border-l border-[#262626] p-6 transition-transform duration-300 ${open ? 'translate-x-0' : 'translate-x-full'}`}>
        <div className="flex items-center justify-between mb-10">
          <Brand small />
          <button onClick={onClose} aria-label="Fermer" className="p-2 -mr-2"><X size={26} /></button>
        </div>
        <nav className="flex flex-col gap-1">
          {NAV_LINKS.map((link) => (
            <div key={link.label}>
              <NavLink to={link.to} className="block py-3 font-cond text-lg tracking-wide text-white hover:text-accent border-b border-[#1d1d1d]">
                {link.label}
              </NavLink>
              {link.children && (
                <div className="pl-4">
                  {link.children.map((c) => (
                    <NavLink key={c.to} to={c.to} className="block py-2 font-cond text-sm tracking-wide text-[var(--hx-muted)] hover:text-accent">
                      {c.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>
        <Link to="/reserver" className="hx-btn w-full justify-center mt-8" data-cta="menu-mobile-reserver">
          Réserver ma séance <ArrowRight size={16} />
        </Link>
        <a
          href="https://member-app.deciplus.pro/fenuafitnessgroup/signIn"
          target="_blank"
          data-cta="menu-mobile-espace-client"
          rel="noopener"
          className="mt-3 block w-full text-center font-cond text-sm tracking-wider px-4 py-3 rounded-md border border-[#333] text-[var(--hx-muted)] hover:text-accent hover:border-accent transition-colors"
        >
          Espace client
        </a>
      </div>
    </div>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [clubsOpen, setClubsOpen] = useState(false);
  const location = useLocation();

  const handleScroll = useCallback(() => setScrolled(window.scrollY > 40), []);

  useEffect(() => {
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    setOpen(false);
    setClubsOpen(false);
  }, [location.pathname]);

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-[#0a0a0a]/95 backdrop-blur-md border-b border-[#222] py-3' : 'bg-transparent py-5'}`}>
      <div className="max-w-7xl mx-auto px-5 lg:px-8 flex items-center justify-between">
        <Brand />
        <DesktopNav clubsOpen={clubsOpen} setClubsOpen={setClubsOpen} />
        <div className="hidden lg:flex items-center gap-3">
          <a
            href="https://member-app.deciplus.pro/fenuafitnessgroup/signIn"
            target="_blank"
            data-cta="header-espace-client"
            rel="noopener"
            className="font-cond text-sm tracking-wider px-4 py-2 rounded-md border border-[#333] text-[var(--hx-muted)] hover:text-accent hover:border-accent transition-colors"
          >
            Espace client
          </a>
          <Link to="/reserver" className="hx-btn text-sm" data-cta="header-essai">
            Séance d'essai <ArrowRight size={16} />
          </Link>
        </div>
        <button className="lg:hidden text-white p-2 -mr-2" onClick={() => setOpen(true)} aria-label="Ouvrir le menu">
          <Menu size={28} />
        </button>
      </div>
      <MobileMenu open={open} onClose={() => setOpen(false)} />
    </header>
  );
}
